'use strict';

(function (global) {
  /**
   * Класс React-элемента страницы.
   *
   * @abstract
   * @extends AbstractBasePageElement
   * @since 5.6.0.1300
   */
  var BaseReactPageElement = WebAccess.components.wrappers.AbstractBasePageElement.extend({

    /**
     * Конструктор.
     *
     * @method BaseReactPageElement#init
     * @param {object} model Модель React-элемента страницы.
     * @fires BaseObject.EVENTS#CLASS_INIT
     */
    init: function (model) {
      this._model = model;
      this.name = this._model.name;
      this._events = this._model.events;
      this.fire(BaseObject.EVENT.CLASS_INIT, this);
      this._formatModel();
      if (this._model.jsAction instanceof Function) {
        this.bind(BaseObject.EVENT.CLICK, this._model.jsAction);
      }
    },

    /**
     * Форматирование модели.
     *
     * @private
     * @method BaseReactPageElement#_formatModel
     */
    _formatModel: function () {
      this._model.onInit = this._formatFunction('onInit');
      this._model.jsAction = this._formatFunction('jsAction');
    },

    /**
     * Преобразование строк в функции.
     *
     * @method BaseReactPageElement#_formatFunction
     * @param {string} paramName Имя параметра из модели.
     * @returns {function|null} Функция или null.
     * @private
     */
    _formatFunction(paramName) {
      let paramValue = this._model[paramName];
      if (paramValue) {
        if (paramValue instanceof Function) {
          return paramValue.bind(this);
        } else if (typeof paramValue === 'string' || paramValue instanceof String) {
          return (new Function(this._model[paramName])).bind(this);
        }
      }
      return null;
    },

    /**
     * Имя класса.
     *
     * @private
     * @property BaseReactPageElement#className
     * @override
     * @see AbstractBasePageElement#className
     */
    className: 'BaseReactPageElement',

    /**
     * Список обработчиков событий.
     *
     * @private
     * @property BaseReactObject#_events
     * @override
     * @see BaseObject#_events
     */
    _events: null,

    /**
     * Модель React-элемента страницы.
     *
     * @private
     * @property BaseReactPageElement#_model
     */
    _model: null,

    /**
     * Наименование элемента.
     *
     * @property BaseReactPageElement#name
     */
    name: '',

    /**
     * Признак скрытого элемента страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#isHidden
     * @return {boolean} True, если элемент скрыт, иначе False.
     * @override
     * @see AbstractBasePageElement#isHidden
     */
    isHidden: function () {
      return !this._model.isVisible;
    },

    /**
     * Признак доступного для изменения элемента страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#isEnabled
     * @return {boolean} True, если элемент отображается, иначе False.
     * @override
     * @see AbstractBasePageElement#isEnabled
     */
    isEnabled: function () {
      return this._model.isEnabled;
    },

    /**
     * Признак выбора элемента страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#isSelected
     * @return {boolean} True, если элемент выбран, иначе False.
     * @override
     * @see AbstractBasePageElement#isSelected
     */
    isSelected: function () {
      return this._model.isSelected;
    },

    /**
     * Отображает элемент страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#show
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @fires BaseReactPageElement#show
     * @override
     * @see AbstractBasePageElement#show
     */
    show: function () {
      this._model.isVisible = true;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.SHOW);
      return this;
    },

    /**
     * Скрывает элемент страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#hide
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @fires BaseReactPageElement#hide
     * @override
     * @see AbstractBasePageElement#hide
     */
    hide: function () {
      this._model.isVisible = false;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.HIDE);
      return this;
    },

    /**
     * Устанавливает заголовок элемента страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#setLabel
     * @param {string} text Заголовок элемента страницы.
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @fires BaseReactPageElement#change
     * @override
     * @see AbstractBasePageElement#setLabel
     */
    setLabel: function (text) {
      this._model.text = text;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.CHANGE);
      return this;
    },

    /**
     * Получает заголовок элемента страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#getLabel
     * @return {string} Заголовок элемента страницы.
     * @override
     * @see AbstractBasePageElement#getLabel
     */
    getLabel: function () {
      return this._model.text;
    },

    /**
     * Устанавливает всплывающую подсказку.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#setTooltip
     * @param {string} text Текст всплывающей подсказки.
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @fires BaseReactPageElement#change
     * @override
     * @see AbstractBasePageElement#setTooltip
     *
     * @todo Подумать над установкой, т.к. у объекта две подсказки, какая устанавливается? Куда сохранять?
     *
     */
    setTooltip: function (text) {
      this._model.tooltip = text;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.CHANGE);
      return this;
    },

    /**
     * Получает текст всплывающей подсказки.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#getTooltip
     * @return {string} Текст всплывающей подсказки.
     * @override
     * @see AbstractBasePageElement#getTooltip
     */
    getTooltip: function () {
      return this._model.tooltip;
    },

    /**
     * Устанавливает иконку.
     *
     * Иконку необходимо разместить в папке проекта App_Sprites и после скомпилировать проект.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#setIcon
     * @param {string} path Путь к иконке.
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @fires BaseReactPageElement#change
     * @override
     * @see AbstractBasePageElement#setIcon
     */
    setIcon: function (path) {
      this._model.icon = path;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.CHANGE);
      return this;
    },

    /**
     * Получает имя иконки.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#getIcon
     * @return {string} Имя иконки.
     * @override
     * @see AbstractBasePageElement#getIcon
     */
    getIcon: function () {
      return this._model.icon;
    },

    /**
     * Имитирует щелчок мыши.
     * Имитация щелчка мыши по элементу страницы либо установка обработчика на событие onClick, если передан opt_callback.
     * Заменяет обработчик, установленный по умолчанию. Используется в прикладной разработке.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#click
     * @param {function} [opt_callback] Функция, которая будет вызвана после щелчка мыши по элементу.
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @override
     * @see AbstractBasePageElement#click
     */
    click: function (opt_callback) {
      if (typeof opt_callback === 'function') {
        this
          .unbind(BaseObject.EVENT.CLICK)
          .bind(BaseObject.EVENT.CLICK, opt_callback);

        this._model.jsAction = opt_callback;
        this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.CHANGE);
      } else {
        this.fire(BaseObject.EVENT.CLICK);
      }

      return this;
    },

    /**
     * Делает элемент страницы недоступным для изменения.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#disable
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @fires BaseReactPageElement#disable
     * @override
     * @see AbstractBasePageElement#disable
     */
    disable: function () {
      this._model.isEnabled = false;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.DISABLE);
      return this;
    },

    /**
     * Делает элемент страницы доступным для изменения.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#enable
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @fires BaseReactPageElement#enable
     * @override
     * @see AbstractBasePageElement#enable
     */
    enable: function () {
      this._model.isEnabled = true;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.ENABLE);
      return this;
    },

    /**
     * Отмечает элемент страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#select
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @override
     * @see AbstractBasePageElement#select
     */
    select: function () {
      this._model.isSelected = true;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.CHANGE);
      return this;
    },

    /**
     * Снимает отметку с элемента страницы.
     *
     * @method WebAccess.components.wrappers.BaseReactPageElement#unSelect
     * @return {WebAccess.components.wrappers.BaseReactPageElement} Объект типа BaseReactPageElement.
     * @override
     * @see AbstractBasePageElement#unSelect
     */
    unSelect: function () {
      this._model.isSelected = false;
      this.fire(WebAccess.components.wrappers.AbstractBasePageElement.EVENT.CHANGE);
      return this;
    }
  });

  global.BaseReactPageElement = BaseReactPageElement;
})(window.WebAccess.components.wrappers);